/** Rich-text editor (react-quill-new / Quill 2) for HTML fields — itinerary day &
 *  slot descriptions, pre-trip items, tag values. Self-contained (bundled JS + CSS,
 *  no external hosts) so it satisfies the console CSP.
 *
 *  Emits HTML; coerces Quill's empty-doc sentinel (`<p><br></p>`) to '' so an
 *  untouched field never reads as dirty and stored values stay clean.
 *
 *  Uncontrolled: Quill is seeded once and owns its document, and only the user's
 *  edits are reported. As a controlled component, react-quill-new reported Quill's
 *  own normalisation of the stored HTML as a change — on mount, for every non-empty
 *  field — and editors that update from a render-time snapshot looped on it until
 *  React's update-depth guard tripped (the tour Itinerary freeze, 2026-10-03). A
 *  change of `value` from outside (discard, reload) re-seeds the editor. */
import 'react-quill-new/dist/quill.snow.css';

import { Box, FormLabel } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';

const MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const FORMATS = ['bold', 'italic', 'underline', 'list', 'link'];

/** Quill emits `<p><br></p>` (and variants) for an empty document. */
export function isHtmlEmpty(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
}

/** Quill 2's semantic export writes every space as `&nbsp;` (`convertHTML` in
 *  quill/core/editor.js), which stops the apps wrapping the text. Every other writer
 *  stores plain spaces. A typed `&nbsp;` is escaped (`&amp;nbsp;`) and survives. */
export function plainSpaces(html: string): string {
  return html.replace(/&nbsp;/g, ' ');
}

/** The Quill instance behind a `QuillEditor` (`onEditor`). */
export type QuillInstance = ReturnType<ReactQuill['getEditor']>;

/** React attaches the ref after `componentDidMount` (which creates the editor); null if
 *  it ever does not exist yet. */
function editorOf(r: ReactQuill): QuillInstance | null {
  try {
    return r.getEditor();
  } catch {
    return null;
  }
}

export function QuillEditor({
  label, value, onChange, readOnly, placeholder, onEditor,
}: {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  /** Opt-in: the Quill instance once mounted (a caret placed by the caller), null when
   *  it goes. */
  onEditor?: (quill: QuillInstance | null) => void;
}) {
  // What the editor holds: the seed, then the user's last edit. Our own emission
  // comes back as `value` and must not re-seed (that would reset the caret).
  const shown = useRef(value);
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    if (value !== shown.current) {
      shown.current = value;
      setGeneration((g) => g + 1);
    }
  }, [value]);

  return (
    <Box>
      {label && (
        <FormLabel sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5, display: 'block' }}>
          {label}
        </FormLabel>
      )}
      <Box
        sx={{
          '& .ql-toolbar': { borderTopLeftRadius: 4, borderTopRightRadius: 4, borderColor: 'divider' },
          '& .ql-container': {
            borderBottomLeftRadius: 4, borderBottomRightRadius: 4, borderColor: 'divider',
            fontFamily: 'inherit', fontSize: 14,
          },
          '& .ql-editor': { minHeight: 96 },
        }}
      >
        <ReactQuill
          key={generation}
          {...(onEditor ? { ref: (r: ReactQuill | null) => onEditor(r ? editorOf(r) : null) } : {})}
          theme="snow"
          defaultValue={shown.current || ''}
          readOnly={readOnly}
          placeholder={placeholder}
          modules={MODULES}
          formats={FORMATS}
          onChange={(html, _delta, source) => {
            if (source !== 'user') return;
            const next = isHtmlEmpty(html) ? '' : plainSpaces(html);
            shown.current = next;
            onChange(next);
          }}
        />
      </Box>
    </Box>
  );
}
